import { serverNow } from '@/lib/clock-skew';
import { LIVE_POLL_MS, isLiveTask, phaseInputOf } from '@/features/offers/request-detail';
import type { TaskRow } from './types';

/**
 * PIYASA SONUCU (2026-10-02) — talep karara baglaninca BU buro icin ne oldu.
 *
 * Sunucu `marketOutcome`'u yalniz piyasa okumasinda (`taskPretenderCurrencyUserId`) ve CAGIRANA
 * gore hesaplar; talep acikken (teklif/karar fazinda 'new') null. Kazananin kimligini tasimaz:
 *   won        : cagiranin teklifi secildi
 *   won_by_org : ayni organizasyondan bir is arkadasinin teklifi secildi
 *   lost       : cagiranin teklifi vardi, baska buro secildi
 *   closed     : cagiranin teklifi yoktu, karar verildi (K3: kimin secildigi soylenmez)
 *   cancelled  : musteri iptal etti
 *   expired    : kimse secilmeden sure doldu
 *
 * Etiket ve aciklama TEK yerde (MARKET_OUTCOME_KEYS): Piyasa Gecmis karti rozeti ve detaydaki
 * sonuc karti ayni metni okur. Anahtar alanlari `*Key` adinda — i18n/keys.test.ts onlari tarar.
 */

export const MARKET_OUTCOMES = [
  'won',
  'won_by_org',
  'lost',
  'closed',
  'cancelled',
  'expired',
] as const;
export type MarketOutcome = (typeof MARKET_OUTCOMES)[number];

/**
 * Ham deger -> sonuc. Bilinmeyen bir deger null'a duser: yeni bir sunucu degeri karti ham
 * anahtarla bozmasin, satir "sonuc yok" gibi cizilsin (eski sunucuda alan hic yok, o da null).
 */
export const asMarketOutcome = (value: unknown): MarketOutcome | null =>
  typeof value === 'string' && (MARKET_OUTCOMES as readonly string[]).includes(value)
    ? (value as MarketOutcome)
    : null;

type OutcomeKeys = { labelKey: string; detailKey: string };

// Anahtar adlari mobil tr.ts ile AYNI (task.marketOutcome.*). 'lost' Tekliflerim kartindaki
// rozetin kendisi (M1, task.badges.lost): iki ekranda ayni sonuc ayni metinle okunur.
const MARKET_OUTCOME_KEYS: Readonly<Record<MarketOutcome, OutcomeKeys>> = Object.freeze({
  won: { labelKey: 'task.marketOutcome.won', detailKey: 'task.marketResult.won' },
  won_by_org: { labelKey: 'task.marketOutcome.wonByOrg', detailKey: 'task.marketResult.wonByOrg' },
  lost: { labelKey: 'task.badges.lost', detailKey: 'task.marketResult.lost' },
  closed: { labelKey: 'task.marketOutcome.closed', detailKey: 'task.marketResult.closed' },
  cancelled: {
    labelKey: 'task.marketOutcome.cancelled',
    detailKey: 'task.marketResult.cancelled',
  },
  // Kimse secilmeden sure doldu: detaydaki eski "teklif toplama suresi doldu" metni burada kalir.
  expired: { labelKey: 'task.marketOutcome.expired', detailKey: 'task.offerWindowClosed' },
});

export type MarketOutcomeView = OutcomeKeys & {
  outcome: MarketOutcome;
  /** Kazanan cercevesi (QuoteCard `won`) — Tekliflerim'deki kazanilan kartla ayni. */
  won: boolean;
  /** Soluk kart (QuoteCard `lost`) — teklif verdi, baska buro secildi (OfferCard M1). */
  lost: boolean;
  /**
   * Faz sayaci bu buroyu ilgilendirmiyor: kart kapali (passive) cizilir. Musterinin odeme
   * sayaci yalniz kazanan icin anlamli; secilmeyen buroda "Odeme Bekliyor" yaniltiyordu (M1).
   */
  closesPhase: boolean;
};

/** Kart ve detay icin sonucun gorunumu; sonuc yoksa (talep acik) null. */
export const marketOutcomeView = (outcome: MarketOutcome | null): MarketOutcomeView | null => {
  if (outcome === null) return null;
  return {
    outcome,
    ...MARKET_OUTCOME_KEYS[outcome],
    won: outcome === 'won',
    lost: outcome === 'lost',
    closesPhase: outcome !== 'won',
  };
};

/**
 * PIYASA DETAYI YOKLAMASI (useTask `pollMs`). Web'e push yok; detay acikken karar ancak
 * yoklamayla gorulur. Talep canliyken (teklif/karar fazinda 'new') 5 sn, sonuc gelince ya da
 * talep artik acik degilse yoklama durur. Satir henuz gelmediyse (undefined) yoklama acik;
 * talep bulunamadiysa (null) kapali. Musteri detayindaki desen (RequestOffersPage taskPollMs).
 */
export const marketTaskPollMs = (
  task: TaskRow | null | undefined,
  nowMs: number = serverNow(),
): number | false => {
  if (task === undefined) return LIVE_POLL_MS;
  if (task === null || task.marketOutcome !== null) return false;
  return isLiveTask(phaseInputOf(task, nowMs), nowMs) ? LIVE_POLL_MS : false;
};
